import { BaseNode, type NodeConfig } from 'mflows'
import type { Variable, VariableType } from 'mflows'
import { variablesState, addGlobalVariable, addWorkflowVariable, updateGlobalVariable, updateWorkflowVariable } from 'mflows'

interface VariableSetConfig extends NodeConfig {
  scope?: 'global' | 'workflow'
  key?: string
  type?: VariableType
  value?: any
}

interface VariableSetValue {
  success: boolean
  variable?: Variable
  message?: string
}

export class VariableSet extends BaseNode<VariableSetConfig, VariableSetValue> {
  static NodeType = '变量设置'

  protected async initialize(): Promise<void> {
    this.setInputPorts([
      { id: 'execute', name: '执行', allowTypes: ['flow'], dataType: 'flow' },
      { id: 'variable-key', name: '变量名', allowTypes: ['text', 'string'], dataType: 'string' },
      { id: 'scope', name: '作用域', allowTypes: ['text', 'string'], dataType: 'string' },
      { id: 'variable-type', name: '类型', allowTypes: ['text', 'string'], dataType: 'string' },
      { id: 'variable-value', name: '值', dataType: 'any' }
    ])

    this.setOutputPorts([
      { id: 'result', name: '结果', dataType: 'object' },
      { id: 'variable-error', name: '错误', dataType: 'object' },
      { id: 'next', name: '下一步', dataType: 'flow' }
    ])
  }

  protected receiveData(pointId: string, data: unknown): void {
    switch (pointId) {
      case 'variable-key':
        if (typeof data === 'string' && data) {
          this.updateSettings({ key: data })
        }
        break
      case 'scope':
        if (data === 'global' || data === 'workflow') {
          this.updateSettings({ scope: data })
        }
        break
      case 'variable-type':
        if (typeof data === 'string') {
          this.updateSettings({ type: data as VariableType })
        }
        break
      case 'variable-value':
        this.updateSettings({ value: data })
        break
    }

    const inputPoint = this.findInputPort(pointId)
    if (inputPoint?.dataType === 'flow') {
      this.executeConfigUpdate()
    }
  }

  protected checkConfig(): boolean {
    const s = this.settings
    return Boolean(s.key && (s.scope === 'global' || s.scope === 'workflow') && s.type)
  }

  private parseValueByType(raw: any, type: VariableType): any {
    try {
      if (type === 'number') return typeof raw === 'number' ? raw : Number(raw)
      if (type === 'boolean') return typeof raw === 'boolean' ? raw : String(raw).toLowerCase() === 'true'
      if (type === 'json') {
        if (typeof raw === 'string') {
          try { return JSON.parse(raw) } catch { return raw }
        }
        return raw
      }
      // string/color/vec2/vec3 直接返回
      return raw
    } catch {
      return raw
    }
  }

  protected async processConfigUpdate(settings: VariableSetConfig): Promise<any> {
    try {
      const { key, scope, type } = settings
      if (!key || !(scope === 'global' || scope === 'workflow') || !type) {
        const err = { message: '请配置变量名、作用域与类型', key, scope, type }
        return { value: err as any, error: new Error(JSON.stringify(err)), state: 'error', updateTime: Date.now() }
      }

      const value = this.parseValueByType(settings.value, type)
      const list = scope === 'global' ? variablesState.globalVariables : variablesState.workflowVariables
      const exists = list.find(v => v.key === key)
      const variable: Variable = { key, type, value }

      try {
        if (exists) {
          scope === 'global' ? updateGlobalVariable(variable) : updateWorkflowVariable(variable)
        } else {
          scope === 'global' ? addGlobalVariable(variable) : addWorkflowVariable(variable)
        }
      } catch (e) {
        const err = { message: (e as any)?.message || '变量写入失败', key, scope }
        return { value: err as any, error: new Error(JSON.stringify(err)), state: 'error', updateTime: Date.now() }
      }

      const result: VariableSetValue = { success: true, variable }
      Object.assign(this.nodeData, { value: result, error: undefined, state: 'dataChange', updateTime: Date.now() })
      return this.nodeData
    } catch (error) {
      return {
        value: { message: String(error) } as any,
        error: error instanceof Error ? error : new Error(String(error)),
        state: 'error',
        updateTime: Date.now()
      }
    }
  }

  protected getOutputPortActions(): { dataOnly?: string[]; triggerFlow?: string[] } {
    if (this.getState() === 'error') {
      return { dataOnly: ['variable-error'], triggerFlow: [] }
    }
    return { dataOnly: ['result'], triggerFlow: ['next'] }
  }
}